import type { Theme } from '@mui/material/styles/createTheme';
import type { FC, ReactNode } from 'react';

import { styled } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { withAuthGuard } from 'src/hocs/with-auth-guard';
import { MobileNav } from './mobile-nav';
import { SideNav } from './vertical-layout/side-nav';
import { TopNav } from './vertical-layout/top-nav';
import { useMobileNav } from './vertical-layout/use-mobile-nav';
import { useSettings } from 'src/hooks/use-settings';

const SIDE_NAV_WIDTH = 280;
const COLLAPSED_SIDE_NAV_WIDTH = 88;

const VerticalLayoutRoot = styled('div')<{ sidebarCollapsed: boolean }>(
  ({ theme, sidebarCollapsed }) => ({
    display: 'flex',
    flex: '1 1 auto',
    maxWidth: '100%',
    [theme.breakpoints.up('lg')]: {
      paddingLeft: sidebarCollapsed ? COLLAPSED_SIDE_NAV_WIDTH : SIDE_NAV_WIDTH,
      transition: theme.transitions.create('padding', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
      }),
    },
  }),
);

const VerticalLayoutContainer = styled('div')({
  display: 'flex',
  flex: '1 1 auto',
  flexDirection: 'column',
  width: '100%',
});

interface LayoutProps {
  children?: ReactNode;
}

export const Layout: FC<LayoutProps> = withAuthGuard((props: any) => {
  const settings = useSettings();
  const { sidebarCollapsed } = settings;

  const lgUp = useMediaQuery((theme: Theme) => theme.breakpoints.up('lg'));
  const mobileNav = useMobileNav();

  return (
    <>
      <TopNav onMobileNavOpen={mobileNav.handleOpen} />
      {lgUp && <SideNav color={settings.navColor} />}
      {!lgUp && (
        <MobileNav
          color={settings.navColor}
          onClose={mobileNav.handleClose}
          open={mobileNav.open}
        />
      )}
      <VerticalLayoutRoot sidebarCollapsed={sidebarCollapsed}>
        <VerticalLayoutContainer>{props.children}</VerticalLayoutContainer>
      </VerticalLayoutRoot>
    </>
  );
});
